import { useMutation } from '@apollo/client';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
	Button,
	Input,
	Popup,
	PopupSection,
	Select,
	SelectOption,
	Text,
} from '@flixbus/honeycomb-react';

import { DEFAULT_FROM_TIME_VALUE } from 'common/activeElements/modals/hooks/const';
import { standardError, standardSuccess } from 'global/commonMessages.js';
import { notificationStore } from 'global/NotificationStore.js';
import { resetTimestampToUtc, updateDateWithTime } from 'utils/datetime';

import CREATE_EMPLOYMENT from './addEmploymentTypeMutation.js';
import { DEFAULT_VALUES, EMPLOYMENT_TYPE_LABEL } from './const.js';

import styles from './styles.module.css';

/**
 * PROPS (injected by ActiveElements + page):
 * - employeeId: string (required)
 * - readOnly?: boolean (default false)
 * - initialData?: { from?: string, fromTime?: string, employmentType?: string }
 * - onActionSuccess?: () => void
 * - onClose: () => void   (injected by RootActiveElementsHost)
 */

const ModalAddEmploymentType = ({
	employeeId,
	readOnly = false,
	initialData = DEFAULT_VALUES,
	onActionSuccess = () => {},
	onClose,
}) => {
	const { control, getValues, handleSubmit, setValue, reset, watch } = useForm({
		mode: 'onChange',
		defaultValues: initialData,
	});

	const [createEmploymentType, { loading }] = useMutation(CREATE_EMPLOYMENT, {
		onCompleted: () => {
			handleResetForm();
			notificationStore.setNotification(standardSuccess);
			onActionSuccess();
			onClose();
		},
		onError: () => {
			notificationStore.setNotification(standardError);
			reset(DEFAULT_VALUES);
		},
		refetchQueries: ['GetCrewMemberEmployment', 'GetCrewMemberCurrentEmployment'],
	});

	const createData = () => {
		const data = getValues();
		const { from, fromTime, employmentType } = data;
		const newFrom = updateDateWithTime(from, fromTime);

		return {
			crewId: employeeId,
			input: {
				effectiveTimestamp: resetTimestampToUtc(newFrom),
				employmentType,
			},
		};
	};

	const confirm = () => {
		const input = createData();
		createEmploymentType({ variables: input });
	};

	const handleResetForm = () => {
		reset(DEFAULT_VALUES);
		onClose();
	};

	const watchedFrom = watch('from');
	const watchedFromTime = watch('fromTime');
	const watchedEmploymentType = watch('employmentType');

	const isValidForm = watchedFrom && watchedEmploymentType && watchedFromTime;

	return (
		<Popup
			extraClasses={styles.modalWrapper}
			id="modal-employment-type"
			active
			onOverlayClick={handleResetForm}
		>
			<PopupSection type="title">Add employment change</PopupSection>

			<PopupSection extraClasses={styles.antiScroll} type="content">
				<Text extraClasses={styles.margin}>
					Specify when the new employment type should take effect
				</Text>

				<form onSubmit={handleSubmit(confirm)}>
					<div className={`${styles.row} ${styles.full}`}>
						<Controller
							name="employmentType"
							control={control}
							rules={{ required: 'Employment type required' }}
							render={({ field }) => (
								<Select
									{...field}
									id="employment-type"
									label="Employment type"
									placeholder="Please select"
									disabled={readOnly}
								>
									{Object.values(EMPLOYMENT_TYPE_LABEL).map((label) => (
										<SelectOption key={label.value} value={label.value}>
											{label.key}
										</SelectOption>
									))}
								</Select>
							)}
						/>
					</div>

					<div className={styles.timeWrapper}>
						<Controller
							name="from"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<Input
									{...field}
									id="employment-from-input"
									type="date"
									name="from"
									label="Start date and time"
									readOnly={readOnly}
									onChange={(e) => {
										field.onChange(e.target.value);
										setValue('fromTime', DEFAULT_FROM_TIME_VALUE, {
											shouldDirty: true,
										});
									}}
								/>
							)}
						/>

						<Controller
							name="fromTime"
							control={control}
							rules={{ required: true }}
							disabled={getValues().from === '' || readOnly}
							render={({ field }) => (
								<Input
									{...field}
									ref={null}
									id="from-time"
									name="fromTime"
									type="time"
									readOnly={readOnly}
									onChange={(e) => field.onChange(e.target.value)}
								/>
							)}
						/>
					</div>
				</form>
			</PopupSection>

			<PopupSection type="actions" extraClasses={styles.modalAbsenceWrapperGridItem}>
				<Button onClick={handleResetForm}>Cancel</Button>

				{!readOnly && (
					<Button
						appearance="primary"
						disabled={!isValidForm}
						type="submit"
						onClick={confirm}
						loading={loading}
					>
						Confirm
					</Button>
				)}
			</PopupSection>
		</Popup>
	);
};

export default ModalAddEmploymentType;
